import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MessageContentDialogComponent } from '../message-content-dialog/message-content-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { CommonDataService } from '../../../../services/common-data.service';
import { UserNotificationItem } from '../../../../models/dtos/Classes/UserNotificationItem';
import { IMessageData } from '../../../../models/common/IMessageData';
import { NotificationApiService } from '../../../../services/api/notification-api.service';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { SetNotificationReadStatusRequest, DeleteNotificationForUserRequest } from '../../../../models/dtos';
import {MatMenuModule, MatMenuTrigger} from '@angular/material/menu';
import {MultilingualDatePipe} from "../../../../pipes/multilingual-date-pipe/multilingual-date-pipe";

@Component({
  selector: 'app-message-dialog',
  imports: [MatDialogModule,
    MatTableModule,
    MatIconModule,
    MatCheckboxModule,
    TranslatePipe, 
    CommonModule,
    MatMenuModule,
    MultilingualDatePipe
  ],
  templateUrl: './message-dialog.component.html',
  styleUrl: './message-dialog.component.scss'
})
export class MessageDialogComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['delete', 'message', 'date'];
  dialogPosition: { top: string } = { top: '124px' };
  menuPosition: { x: string, y: string } = { x: '0px', y: '0px' };
  messageDataSource = new MatTableDataSource<IMessageData>([]);
  selectedRow: IMessageData | null = null;

  selection = new SelectionModel<IMessageData>(true, []);
  private destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<MessageDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly commonDataService: CommonDataService,
    private readonly notificationApiService: NotificationApiService,
    private readonly userProfileService: UserProfileService
  ) { }

  ngOnInit(): void {
    this.getNotificationMessages();
  }

  getNotificationMessages(): void {
    const notifications = this.commonDataService.getNotifications();
    if (notifications && notifications.length) {
      this.assignMessageDataToDataSource(notifications);
    }
  }

  onRowClick(row: IMessageData): void {
    this.selectedRow = row;
  }

  isRowSelected(row: IMessageData): boolean {
    return this.selectedRow === row;
  }

  assignMessageDataToDataSource(notifications: UserNotificationItem[]) {
    const messageDataList: IMessageData[] = [];

    for (const notification of notifications) {
      messageDataList.push({
        messageId: notification.messageId,
        message: notification.subject,
        date: notification.messageDateTime.toString(),
        read: notification.isRead,
        body: notification.message
      });
    }

    this.messageDataSource.data = messageDataList;
  }


 checkboxLabel(row: IMessageData): string {
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.message}`;
  }


  openMessageContent(selectedRow: IMessageData) {
    this.updateReadStatus([selectedRow], true);

    const dialogConfig = new MatDialogConfig();   
    dialogConfig.position = { ...this.dialogPosition };
    dialogConfig.width = '900px';
    dialogConfig.height = '500px';
    dialogConfig.disableClose = true;
    dialogConfig.data = { title: selectedRow.message, body: selectedRow.body, createdOn: '' };

    this.dialog.open(MessageContentDialogComponent, dialogConfig);
    
  }

  private updateReadStatus(rows: IMessageData[], isRead: boolean): void {
    const changedRows = rows.filter(
      (row): row is IMessageData & { messageId: number } => row.messageId !== undefined && row.read !== isRead
    );
    if (!changedRows.length) {
      return;
    }

    const request: SetNotificationReadStatusRequest = {
      userId: this.userProfileService.userInfo?.gid || '',
      messageIds: changedRows.map(row => row.messageId).join(','),
      isRead
    };

    this.notificationApiService.setNotificationReadStatus(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const messageIds = new Set(changedRows.map(row => row.messageId));
          const notifications = this.commonDataService.getNotifications().map(notification =>
            messageIds.has(notification.messageId) ? { ...notification, isRead } : notification
          );
          this.commonDataService.setNotifications(notifications);
          changedRows.forEach(row => row.read = isRead);
          this.messageDataSource.data = [...this.messageDataSource.data];
        },
        error: (error) => {
          console.error('Error updating notification read status:', error);
        }
      });
  }

  onRightClick(event: MouseEvent, menuTrigger: MatMenuTrigger, row: IMessageData): void {
    event.preventDefault();
    event.stopPropagation(); 
    this.selectedRow = row;
    this.menuPosition.x = event.clientX + 'px';
    this.menuPosition.y = event.clientY + 'px';
    menuTrigger.openMenu();
  }

  deleteSelectedRow(row: IMessageData): void {
    if(!row || row.messageId === undefined) {
      return;
    }

    const request: DeleteNotificationForUserRequest = {
      userId: this.userProfileService.userInfo?.gid || '',
      messageIds: row.messageId.toString()
    };

    this.notificationApiService.deleteNotificationForUser(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const remainingNotifications = this.commonDataService.getNotifications().filter(
            notification => notification.messageId !== row.messageId
          );
          this.commonDataService.setNotifications(remainingNotifications);
          this.selection.deselect(row);
          this.selectedRow = null;
          this.assignMessageDataToDataSource(remainingNotifications);
        },
        error: (error) => {
          console.error('Error deleting notification:', error);
        }
      });
  }
  
  markAsUnread(row: IMessageData): void {
    if(!row) {
      return;
    }

    this.updateReadStatus([row], false);
  }

  markAllAsRead(): void {
    this.updateReadStatus(this.messageDataSource.data, true);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeElement() {
    if (!this.selection.selected.length) {
      this.dialogRef.close();
      return;
    }

    const notifications = this.commonDataService.getNotifications();
    const selectedItems = this.selection.selected;
    const selectedMessageIds = selectedItems.map(item => item.messageId).join(',');

    const request: DeleteNotificationForUserRequest = {
      userId: this.userProfileService.userInfo?.gid || '',
      messageIds: selectedMessageIds
    };

    this.notificationApiService.deleteNotificationForUser(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const remainingNotifications = notifications.filter(
            n => !selectedItems.some(item => item.messageId === n.messageId)
          );
          this.commonDataService.setNotifications([...remainingNotifications]);
          this.selection.clear();
          this.assignMessageDataToDataSource(remainingNotifications);
          this.dialogRef.close();
        },
        error: (error) => {
          console.error('Error deleting notification:', error);
          this.dialogRef.close();
        }
      });
  }
  onSelectionChange(event: MatCheckboxChange, element: IMessageData): void {
    if(!event)
    {
      return;
    }
    if (event.checked) {
      this.selection.select(element);
    } else {
      this.selection.deselect(element);
    }
  }
}